import type { Cart } from '@repo/shopify/types';
import type { I18nConfig } from '@shopify/hydrogen';
import { getTrackingValues } from '@shopify/hydrogen-react';
import { readBrowserControlTrackingValues } from './browser-cart-control';
import { addBrowserCartLines, type BrowserCartTransportOptions } from './browser-cart-transport';

export type BrowserCartAddContext = Omit<BrowserCartTransportOptions, 'lines'>;

/** Select the transport before mutation; an uncertain direct result is never retried. */
export function getBrowserCartAddContext(input: {
	cartId: string | null;
	cartCountryCode?: string | null;
	countryCode: string;
	languageCode: string;
}): BrowserCartAddContext | undefined {
	if (
		typeof window === 'undefined' ||
		process.env.NEXT_PUBLIC_SHOPIFY_BROWSER_CART_TRANSPORT === '0' ||
		!/^[A-Z]{2}$/.test(input.countryCode) ||
		!/^[A-Z]{2}(?:_[A-Z]{2})?$/.test(input.languageCode) ||
		(input.cartId && input.cartCountryCode !== input.countryCode)
	)
		return;
	const config = {
		storeDomain: process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN ?? '',
		storefrontId: process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ID ?? '',
		publicToken: process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN ?? '',
	};
	if (
		!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(config.storeDomain) ||
		!/^\d+$/.test(config.storefrontId) ||
		!config.publicToken.trim()
	)
		return;
	try {
		const trackingValues = readBrowserControlTrackingValues({
			privacy: window.Shopify?.customerPrivacy,
			entries: performance.getEntriesByType('resource'),
			origin: window.location.origin,
			getTrackingValues,
		});
		if (!trackingValues) return;
		return {
			cartId: input.cartId ?? undefined,
			config,
			trackingValues,
			pageUrl: window.location.href,
			i18n: {
				country: input.countryCode,
				language: input.languageCode,
			} as I18nConfig,
		};
	} catch {
		// Consent initialization can be unavailable. Choose the existing server
		// path before creating anything so shopping still works without consent.
		return;
	}
}

export async function addCartLinesWithSelectedTransport(options: {
	context: BrowserCartAddContext | undefined;
	lines: BrowserCartTransportOptions['lines'];
	serverAdd(): Promise<Cart>;
}): Promise<Cart> {
	if (!options.context) return options.serverAdd();
	return addBrowserCartLines({ ...options.context, lines: options.lines });
}
